import React, { useEffect, useState } from "react";
import SafeIcon from "../../../common/SafeIcon";
import * as FiIcons from "react-icons/fi";
import ClientInvoiceStatusBadge from "../invoices/ClientInvoiceStatusBadge";
import { clientBillService } from "../../../services/client/clientBillService";

const { FiFileText, FiX } = FiIcons;

function money(value, currency) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "Not available";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount);
  } catch {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(amount);
  }
}

function dateLabel(value) {
  if (!value) return "Not provided";
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

function Detail({ label, value }) {
  return (
    <div className="min-w-0 rounded-xl bg-slate-50 p-3">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
      <p className="mt-1 break-words text-sm font-semibold text-slate-800">{value || "Not provided"}</p>
    </div>
  );
}

export default function ClientBillDetailsModal({ organizationId, bill: selectedBill, onClose }) {
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!selectedBill || !organizationId) {
      setBill(null);
      return undefined;
    }
    let current = true;
    setBill(null);
    setLoading(true);
    setError("");
    clientBillService
      .getClientBillDetails({ organizationId, billId: selectedBill.id })
      .then((details) => current && setBill(details))
      .catch((loadError) => current && setError(loadError?.message || "Unable to load bill details."))
      .finally(() => current && setLoading(false));
    return () => { current = false; };
  }, [organizationId, selectedBill?.id]);

  useEffect(() => {
    if (!selectedBill) return undefined;
    const closeOnEscape = (event) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [selectedBill, onClose]);

  if (!selectedBill) return null;
  const displayed = bill || selectedBill;
  const currency = displayed.currency || "USD";
  const items = displayed.items || [];
  const payments = displayed.payments || [];

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center bg-slate-950/50 p-0 backdrop-blur-sm sm:items-center sm:p-4" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section role="dialog" aria-modal="true" aria-labelledby="client-bill-title" className="max-h-[94dvh] w-full overflow-y-auto rounded-t-3xl bg-white shadow-2xl sm:max-w-3xl sm:rounded-3xl">
        <header className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-slate-100 bg-white px-5 py-4 sm:px-7">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-violet-700">Read-only bill</p>
            <h2 id="client-bill-title" className="mt-1 break-words text-xl font-black text-slate-900">{displayed.bill_number || "Bill details"}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close bill details" className="rounded-xl p-2 text-slate-500 hover:bg-slate-100"><SafeIcon icon={FiX} className="text-lg" /></button>
        </header>
        <div className="space-y-5 p-5 sm:p-7">
          {loading && <p className="text-sm text-slate-500">Loading bill details…</p>}
          {error && <p role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</p>}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <ClientInvoiceStatusBadge status={displayed.displayStatus || displayed.status} />
            <span className="text-xs text-slate-500">Currency: {currency}</span>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Detail label="Vendor" value={displayed.vendor?.vendor_name || displayed.vendor?.company_name} />
            <Detail label="Bill date" value={dateLabel(displayed.bill_date)} />
            <Detail label="Due date" value={dateLabel(displayed.due_date)} />
            <Detail label="Bill number" value={displayed.bill_number} />
          </div>
          {displayed.notes && <section className="rounded-xl border border-slate-200 p-4"><h3 className="text-xs font-bold uppercase text-slate-400">Notes</h3><p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{displayed.notes}</p></section>}
          <section className="overflow-hidden rounded-2xl border border-slate-200">
            <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-3">
              <SafeIcon icon={FiFileText} className="text-violet-600" />
              <h3 className="text-sm font-bold text-slate-800">Line items</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px] text-left text-sm">
                <thead className="text-[10px] font-bold uppercase text-slate-400"><tr><th className="px-4 py-3">Description</th><th className="px-4 py-3 text-right">Qty</th><th className="px-4 py-3 text-right">Unit price</th><th className="px-4 py-3 text-right">Tax</th><th className="px-4 py-3 text-right">Line total</th></tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((item) => <tr key={item.id}><td className="px-4 py-3 font-medium text-slate-800">{item.description || item.item || "Item"}</td><td className="px-4 py-3 text-right">{item.quantity ?? item.qty ?? "—"}</td><td className="px-4 py-3 text-right">{money(item.unit_price ?? item.price, currency)}</td><td className="px-4 py-3 text-right">{item.tax_rate == null ? "—" : `${item.tax_rate}%`}</td><td className="px-4 py-3 text-right font-semibold">{money(item.amount, currency)}</td></tr>)}
                  {!loading && items.length === 0 && <tr><td colSpan="5" className="px-4 py-8 text-center text-slate-500">No line items available.</td></tr>}
                </tbody>
              </table>
            </div>
          </section>
          <section className="ml-auto w-full max-w-sm space-y-2 rounded-2xl bg-slate-50 p-4 text-sm">
            <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{money(displayed.subtotal, currency)}</span></div>
            <div className="flex justify-between text-slate-600"><span>Taxes</span><span>{money(displayed.tax_total, currency)}</span></div>
            <div className="flex justify-between border-t border-slate-200 pt-2 font-bold text-slate-900"><span>Total</span><span>{money(displayed.total, currency)}</span></div>
            <div className="flex justify-between text-slate-600"><span>Amount paid</span><span>{money(displayed.amount_paid, currency)}</span></div>
            <div className="flex justify-between font-bold text-violet-800"><span>Balance due</span><span>{money(displayed.balance_due, currency)}</span></div>
          </section>
          <section className="rounded-2xl border border-slate-200 p-4">
            <h3 className="text-sm font-bold text-slate-800">Payment history</h3>
            {payments.length ? <div className="mt-2 divide-y divide-slate-100">{payments.map((payment) => <div key={payment.id} className="flex flex-wrap justify-between gap-2 py-3 text-sm"><span className="text-slate-600">{dateLabel(payment.payment_date)} · {(payment.payment_method || "payment").replaceAll("_", " ")}</span><span className="font-semibold">{money(payment.amount, currency)}</span></div>)}</div> : <p className="mt-2 text-sm text-slate-500">No payments are recorded for this bill.</p>}
          </section>
          <p className="text-xs text-slate-500">Receipt attachments are not shown because the required receipt database migration is pending.</p>
        </div>
        <footer className="border-t border-slate-100 px-5 py-4 text-right sm:px-7"><button type="button" onClick={onClose} className="rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-slate-800">Close</button></footer>
      </section>
    </div>
  );
}